import React from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Company,
  PaymentMethod,
  ShopCart,
  TTally,
  PromoCode,
} from '../../models';
import { GET_SHOP_CART_TALLY, VERIFY_PROMO_CODE } from '../../graphql/queries';
import { useNotify, useUser } from '../../hooks';
import Invoice from './Invoice/Invoice';
import PaymentSteps from './PaymentSteps/PaymentSteps';
import CouponInput from '../shopcart/CouponInput';

interface CheckoutProps {
  shopCart: ShopCart;
  paymentMethods: PaymentMethod[];
}

function Checkout({ shopCart, paymentMethods }: CheckoutProps) {
  const notify = useNotify();
  const [user] = useUser();

  const [promoCode, setPromoCode] = React.useState('');
  const [appliedPromoCodes, setAppliedPromoCodes] = React.useState([]);
  const [total, setTotal] = React.useState<TTally>();

  const {
    data: dataTally,
    loading: loadingTally,
    error,
  } = useQuery<{ getShopCartTally: TTally }>(GET_SHOP_CART_TALLY, {
    variables: {
      data: {
        shopCartId: shopCart?._id,
        promoCodes: appliedPromoCodes,
        benefitsApplied: [],
      },
    },
    fetchPolicy: 'network-only',
  });

  const [verifyPromoCode, { loading }] = useLazyQuery<{
    verifyPromoCode: PromoCode;
  }>(VERIFY_PROMO_CODE, {
    fetchPolicy: 'network-only',
  });

  React.useEffect(() => {
    const syncTally = async () => {
      if (loadingTally) return;
      setTotal(dataTally?.getShopCartTally);
    };
    syncTally();
  }, [loadingTally]);

  const handleApplyPromoCode = async () => {
    try {
      const { data } = await verifyPromoCode({
        variables: {
          data: {
            code: promoCode,
            clientId: (user?.company as Company)?._id,
          },
        },
      });
      if (
        data?.verifyPromoCode._id &&
        !appliedPromoCodes.includes(data?.verifyPromoCode)
      ) {
        setAppliedPromoCodes([
          ...appliedPromoCodes,
          data?.verifyPromoCode.code,
        ]);
        setPromoCode('');
        notify('Cupón aplicado', 'success');
      } else {
        notify('Cupón inválido', 'error');
      }
    } catch (error) {
      console.log(error);
      notify(error.message, 'error');
    }
  };

  return (
    <div className="w-full flex flex-wrap gap-5 lg:gap-0">
      <div className="w-full lg:w-2/3 lg:pr-5">
        <PaymentSteps
          paymentMethods={paymentMethods}
          appliedPromoCodes={appliedPromoCodes}
          total={total}
        />
      </div>
      <div className="w-full lg:w-1/3 lg:pl-5 flex flex-col space-y-5">
        <Invoice shopCart={shopCart} total={total} />
        <CouponInput
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          handleApplyPromoCode={handleApplyPromoCode}
          loading={loading}
        />
      </div>
    </div>
  );
}

export default Checkout;
